<template>
  <div class="accruals-data-workspace" data-testid="accruals-data-page__workspace">
    <accruals-entity-tabs
      v-model="activeTabId"
      :has-active-table-filter="hasActiveTableFilter"
      @clear-filter="onClearFilters"
    />

    <accruals-sync-status-bar
      :total-label="syncTotalLabel"
      :total-value="syncTotalValue"
      :last-synced-label="lastSyncedLabel"
      :version-label="versionLabel"
      :cutoff-label="cutoffLabel"
      :loading="loading || syncPolling"
      :can-sync="canSyncCycle"
      :issue-label="ingestIssueLabel"
      :issue-tone="ingestIssueTone"
      @sync="syncNow"
    />

    <div
      v-if="syncPolling"
      class="accruals-data-workspace__sync-progress"
      data-testid="accruals-data-page__sync-progress"
    >
      <div
        class="accruals-data-workspace__sync-track"
        role="progressbar"
        :aria-valuenow="syncProgress"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Cycle sync progress"
      >
        <div
          class="accruals-data-workspace__sync-fill"
          :class="{ 'accruals-data-workspace__sync-fill--waiting': !syncProgress }"
          :style="syncProgress ? { width: `${syncProgress}%` } : null"
        />
      </div>
      <span class="accruals-data-workspace__sync-progress-label">
        {{ syncProgress ? `${syncProgress}%` : 'Syncing…' }}
      </span>
    </div>

    <v-alert
      v-if="poNeedsCycleSync"
      type="warning"
      dense
      text
      class="accruals-data-workspace__error"
      data-testid="accruals-data-page__po-sync-notice"
    >
      Purchase order data has not been synced to this cycle.
    </v-alert>

    <v-alert
      v-if="newerSnapshotPending"
      type="warning"
      dense
      text
      class="accruals-data-workspace__error"
      data-testid="accruals-data-page__newer-snapshot-notice"
    >
      New {{ activeTab.label }} data is in. Estimates on this cycle were computed from the
      previous snapshot. Sync now updates this cycle to the latest version.
    </v-alert>

    <v-alert
      v-if="error"
      type="error"
      dense
      text
      class="accruals-data-workspace__error"
      data-testid="accruals-data-page__table-error"
    >
      {{ error }}
    </v-alert>

    <div class="accruals-data-workspace__table-shell flex-fill-table">
      <lb-datatableaction
        ref="dataTable"
        auto-height
        :headers="tableHeaders"
        :tabledata="tableRows"
        :filter-options-data="filterOptionsRows"
        :displayed-row-count="total"
        :itemsperpage="tableRows.length || itemsPerPage"
        :bulk-actions="false"
        header-bg-class="fbc-table-header-bg"
        data-testid="accruals-data-page__table"
        @filter-item="onFilterItem"
        @clear-item="onClearFilters"
        @filter-active-change="onFilterActiveChange"
      >
        <template #tbody>
          <tr
            v-for="(item, index) in tableRows"
            :key="item._rowKey || index"
            data-testid="accruals-data-page__table-row"
          >
            <td
              v-for="header in visibleHeaders"
              :key="`${header.value}-${index}`"
              class="px-4 single-line"
              :data-testid="`accruals-data-page__cell--${header.value}-${index}`"
            >
              {{ formatCell(item[header.value]) }}
            </td>
          </tr>
          <tr v-if="!loading && !tableRows.length">
            <td
              :colspan="visibleHeaders.length"
              class="px-4 py-6 text-center gray--text"
              data-testid="accruals-data-page__table-empty"
            >
              No published rows yet for this dataset.
            </td>
          </tr>
        </template>
      </lb-datatableaction>
    </div>

    <pagination
      :key="`${activeTabId}-${paginationResetKey}`"
      class="accruals-data-workspace__pagination"
      :list="paginationList"
      :itemsperpage="itemsPerPage"
      data-testid="accruals-data-page__pagination"
      @paginated-data="onPaginatedData"
    />

  </div>
</template>

<script>
import AccrualsEntityTabs from './AccrualsEntityTabs.vue';
import AccrualsSyncStatusBar from './AccrualsSyncStatusBar.vue';
import Pagination from '../../../../components/Pagination/Pagination.vue';
import {
  formatAccrualsColumnLabel,
  filterOptionsToRows,
  getAccrualsTabById,
  isValidDataTabId,
} from '../accrualsDataConstants';

const SYNC_TOTAL_LABELS = {
  open_po: 'Total POs Synced',
  accruals_ledger: 'Total ledger records synced',
  ap_postings: 'Total AP records synced',
  vendor_master: 'Total vendors synced',
  open_invoices: 'Total unapproved invoices synced',
};

const CHUNK_READY_STATUSES = ['chunked', 'awaiting_mapping'];
const CHUNK_POLL_MS = 3000;
const CHUNK_POLL_MAX = 40;
const FLOW_POLL_MS = 5000;
const FLOW_POLL_MAX = 120;
const FLOW_DONE_STATUSES = ['completed', 'failed', 'cancelled'];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: 'AccrualsDataWorkspace',
  components: {
    AccrualsEntityTabs,
    AccrualsSyncStatusBar,
    Pagination,
  },
  data() {
    const routeTab = this.$route?.query?.tab;
    return {
      activeTabId: isValidDataTabId(routeTab) ? routeTab : 'open_po',
      paginationResetKey: 0,
      page: 1,
      itemsPerPage: 25,
      activeFilters: {},
      hasActiveTableFilter: false,
      syncPolling: false,
    };
  },
  beforeDestroy() {
    this.syncPolling = false;
  },
  computed: {
    accrualsState() {
      return this.$store.state.m_financialclose?.accruals || {};
    },
    summary() {
      return this.accrualsState.summary || {};
    },
    flowStatus() {
      return this.accrualsState.flowStatus || {};
    },
    rows() {
      return this.accrualsState.rows || [];
    },
    columns() {
      return this.accrualsState.columns || [];
    },
    filterOptions() {
      return this.accrualsState.filterOptions || {};
    },
    total() {
      return Number(this.accrualsState.total) || 0;
    },
    loading() {
      return !!this.accrualsState.loading;
    },
    error() {
      return this.accrualsState.error;
    },
    projectId() {
      return this.$route.params.id;
    },
    canIngest() {
      return Boolean(this.accrualsState.access?.canIngest);
    },
    cid() {
      return this.$route.query.cid || this.$store.state.client;
    },
    entitySummary() {
      return this.summary?.entities || {};
    },
    activeTab() {
      return getAccrualsTabById(this.activeTabId);
    },
    entityType() {
      return this.activeTab.entityType;
    },
    newerSnapshotPending() {
      if (this.loading || this.entityType !== 'open_po') return false;
      return Boolean(this.entitySummary.open_po?.newer_snapshot_pending);
    },
    canSyncCycle() {
      return this.canIngest && this.entityType === 'open_po';
    },
    entityIngest() {
      return this.entitySummary[this.entityType]?.ingest || {};
    },
    ingestIssueLabel() {
      return this.entityIngest.message || '';
    },
    ingestIssueTone() {
      return this.entityIngest.outcome === 'completed_with_rejects' ? 'warning' : 'error';
    },
    poNeedsCycleSync() {
      if (this.entityType !== 'open_po' || this.loading) return false;
      if (this.ingestIssueLabel) return false;
      const summary = this.entitySummary.open_po || {};
      const count = Number(summary.canonical_count) || 0;
      if (count > 0 || summary.active_version_id) return false;
      const status = this.flowStatus?.entities?.open_po?.status;
      return status !== 'in-progress' && status !== 'in_progress';
    },
    syncTotalLabel() {
      return SYNC_TOTAL_LABELS[this.entityType] || 'Total records synced';
    },
    syncTotalValue() {
      const count = this.entitySummary[this.entityType]?.canonical_count;
      return this.formatCount(count);
    },
    lastSyncedLabel() {
      const entity = this.entitySummary[this.entityType] || {};
      const count = Number(entity.canonical_count) || 0;
      if (entity.ingest?.outcome === 'failed' && !count) return '';
      if (!count && !entity.active_version_id) return '';
      const syncedAt = entity.last_synced_at;
      if (!syncedAt) return '';
      const date = new Date(syncedAt);
      if (Number.isNaN(date.getTime())) return '';
      return this.$nova.formatDateTime(date);
    },
    versionLabel() {
      const entity = this.entitySummary[this.entityType] || {};
      const count = Number(entity.canonical_count) || 0;
      if (entity.ingest?.outcome === 'failed' && !count) return '';
      const version = entity.active_version_id;
      return version ? String(version) : '';
    },
    syncProgress() {
      const value = Number(this.flowStatus?.entities?.[this.entityType]?.progress);
      if (!this.syncPolling || Number.isNaN(value)) return 0;
      return Math.max(0, Math.min(100, Math.round(value)));
    },
    cutoffLabel() {
      const endDate = this.summary?.period_end_date || this.summary?.cutoff_date;
      if (!endDate) return 'Cut-off: —';
      const formatted = this.$nova.formatDate
        ? this.$nova.formatDate(endDate)
        : String(endDate);
      return `Cut-off: ${formatted}`;
    },
    tableHeaders() {
      const dataHeaders = this.columns.map((column) => ({
        text: formatAccrualsColumnLabel(column),
        value: column,
        visible: true,
        sortable: false,
        filterable: true,
      }));
      return [
        {
          text: 'S.NO',
          value: 'sno',
          visible: true,
          sortable: false,
          filterable: false,
        },
        ...dataHeaders,
      ];
    },
    visibleHeaders() {
      return this.tableHeaders.filter((header) => header.visible !== false);
    },
    tableRows() {
      const offset = (this.page - 1) * this.itemsPerPage;
      return this.rows.map((row, index) => ({
        ...row,
        sno: offset + index + 1,
        _rowKey: row._id || `${this.entityType}-${offset + index}`,
      }));
    },
    filterOptionsRows() {
      return filterOptionsToRows(this.filterOptions);
    },
    paginationList() {
      const count = Number(this.total) || 0;
      return Array.from({ length: count }, (_, index) => ({ _paginationIndex: index }));
    },
  },
  watch: {
    '$route.query.tab'(tab) {
      this.syncTabFromRoute(tab);
    },
    activeTabId(tabId, previousTabId) {
      if (tabId === previousTabId) return;
      this.resetTabState();
      if (this.$route.query.tab !== tabId) {
        this.$nova.gotoLink({
          path: this.$route.path,
          addquery: { tab: tabId },
        });
      }
      this.loadWorkspaceData();
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    formatCount(value) {
      const count = Number(value) || 0;
      return this.$nova.formatNumber ? this.$nova.formatNumber(count) : String(count);
    },
    formatCell(value) {
      if (value === null || value === undefined || value === '') return '—';
      return value;
    },
    resetTabState() {
      this.page = 1;
      this.paginationResetKey += 1;
      this.activeFilters = {};
      this.hasActiveTableFilter = false;
    },
    syncTabFromRoute(tab) {
      const tabId = isValidDataTabId(tab) ? tab : 'open_po';
      if (this.activeTabId === tabId) return;
      this.activeTabId = tabId;
    },
    syncNow() {
      if (!this.canSyncCycle || this.syncPolling) return;
      this.runCycleSync(this.entityType);
    },
    async bootstrap() {
      await this.$store.dispatch('accruals/loadAccess', { projectId: this.projectId });
      await Promise.all([
        this.$store.dispatch('accruals/loadSummary', {
          projectId: this.projectId,
          cid: this.cid,
        }),
        this.$store.dispatch('accruals/loadFlowStatus', {
          projectId: this.projectId,
          cid: this.cid,
        }),
      ]);
      await this.loadWorkspaceData();
    },
    async loadWorkspaceData() {
      await Promise.all([
        this.fetchTableData(),
        this.$store.dispatch('accruals/loadSourceDataFilterOptions', {
          projectId: this.projectId,
          cid: this.cid,
          entityType: this.entityType,
        }),
      ]);
    },
    async fetchTableData() {
      await this.$store.dispatch('accruals/loadSourceData', {
        projectId: this.projectId,
        cid: this.cid,
        entityType: this.entityType,
        page: this.page,
        limit: this.itemsPerPage,
        filters: this.activeFilters,
      });
    },
    async refreshAfterSync() {
      await Promise.all([
        this.$store.dispatch('accruals/loadSummary', {
          projectId: this.projectId,
          cid: this.cid,
        }),
        this.$store.dispatch('accruals/loadFlowStatus', {
          projectId: this.projectId,
          cid: this.cid,
        }),
      ]);
      await this.loadWorkspaceData();
    },
    async refreshCurrentTab() {
      if (this.syncPolling) return;
      this.$store.commit('accruals/setError', null);
      this.$emit('refreshing', true);
      try {
        await this.refreshAfterSync();
        this.$store.commit('sbSuccess', 'Data refreshed', { root: true });
      } finally {
        this.$emit('refreshing', false);
      }
    },
    /**
     * Bind the current cycle to the client source file and start ingestion
     * once chunks exist and column mapping is complete.
     */
    async runCycleSync(trackedEntity) {
      this.syncPolling = true;
      this.$store.commit('accruals/setError', null);

      try {
        let uploadStatus = '';
        let sawUpload = false;
        for (let attempt = 0; attempt < CHUNK_POLL_MAX && this.syncPolling; attempt += 1) {
          const headers = await this.$store.dispatch('accruals/loadSourceHeaders', {
            projectId: this.projectId,
            cid: this.cid,
            entityType: trackedEntity,
          });
          uploadStatus = headers?.upload_status || '';
          if (uploadStatus) sawUpload = true;
          if (uploadStatus === 'header_mismatch' || CHUNK_READY_STATUSES.includes(uploadStatus)) {
            break;
          }
          if (!uploadStatus) break;
          await sleep(CHUNK_POLL_MS);
        }

        if (!sawUpload) {
          this.$store.commit(
            'sbError',
            'Upload a source file on Data Configuration before syncing this cycle.',
            { root: true }
          );
          return;
        }

        if (uploadStatus === 'header_mismatch') {
          this.$store.commit(
            'sbError',
            'Source file headers do not match the saved column mapping. Update the mapping on Data Configuration, then re-upload.',
            { root: true }
          );
          return;
        }

        if (!CHUNK_READY_STATUSES.includes(uploadStatus)) {
          this.$store.commit(
            'sbError',
            'Upload is still "landed" — accruals-s3-chunker did not finish. Check the Lambda, then Sync now again.',
            { root: true }
          );
          return;
        }

        const normalization = await this.$store.dispatch('accruals/loadNormalizationConfig', {
          projectId: this.projectId,
          cid: this.cid,
          entityType: trackedEntity,
        });
        if (!normalization?.mapping_complete) {
          this.$store.commit(
            'sbError',
            'Set column mapping on Data Configuration before syncing this cycle.',
            { root: true }
          );
          return;
        }

        try {
          await this.$store.dispatch('accruals/startIngestion', {
            projectId: this.projectId,
            cid: this.cid,
            entityType: trackedEntity,
          });
        } catch (err) {
          const message = String(err?.response?.data?.message || err?.message || '');
          const alreadyRunning =
            /in[- ]progress/i.test(message) || /already/i.test(message) || /conflict/i.test(message);
          if (!alreadyRunning) throw err;
          this.$store.commit('accruals/setError', null);
        }

        let flowStatus = '';
        for (let attempt = 0; attempt < FLOW_POLL_MAX && this.syncPolling; attempt += 1) {
          await this.$store.dispatch('accruals/loadFlowStatus', {
            projectId: this.projectId,
            cid: this.cid,
          });
          flowStatus =
            this.accrualsState.flowStatus?.entities?.[trackedEntity]?.status || '';
          if (FLOW_DONE_STATUSES.includes(flowStatus)) break;
          await sleep(FLOW_POLL_MS);
        }

        await this.refreshAfterSync();

        const ingest = this.accrualsState.summary?.entities?.[trackedEntity]?.ingest || {};
        if (flowStatus === 'failed' || ingest.outcome === 'failed') {
          this.$store.commit(
            'sbError',
            ingest.message || `${trackedEntity} sync failed. No rows were published.`,
            { root: true }
          );
        } else if (ingest.outcome === 'completed_with_rejects') {
          this.$store.commit(
            'sbWarning',
            ingest.message || `${trackedEntity} sync finished with rejected rows.`,
            { root: true }
          );
        } else if (flowStatus === 'completed') {
          this.$store.commit(
            'sbSuccess',
            `${trackedEntity} sync completed.`,
            { root: true }
          );
        } else if (!FLOW_DONE_STATUSES.includes(flowStatus)) {
          this.$store.commit(
            'sbError',
            'Still processing after timeout — refresh the Data tab later.',
            { root: true }
          );
        }
      } catch (err) {
        // Store already toasted API errors.
      } finally {
        this.syncPolling = false;
      }
    },
    onPaginatedData(paginationInfo) {
      const nextPage = Number(paginationInfo?.page) || 1;
      if (nextPage === this.page) return;
      this.page = nextPage;
      this.fetchTableData();
    },
    async onFilterItem(item) {
      const nextFilters = JSON.parse(JSON.stringify(item || {}));
      const nextSig = JSON.stringify(nextFilters);
      const currentSig = JSON.stringify(this.activeFilters || {});
      if (nextSig === currentSig) return;

      this.activeFilters = nextFilters;
      this.page = 1;
      this.paginationResetKey += 1;
      await this.fetchTableData();
    },
    async onClearFilters() {
      this.activeFilters = {};
      this.hasActiveTableFilter = false;
      this.page = 1;
      this.paginationResetKey += 1;
      await this.fetchTableData();
    },
    onFilterActiveChange(active) {
      this.hasActiveTableFilter = !!active;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsDataWorkspace.scss';
</style>
